// frontend/src/components/SessionMonitor.jsx
// 🔒 PRODUCTION VERSION - 60 minute inactivity timeout for financial security
// ✅ FIXED: Multi-tab synchronization - detects when another tab logs out

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const CHECK_INTERVAL    = 60 * 1000;        // check every 60 seconds
const TOKEN_REFRESH_THRESHOLD = 50 * 60 * 1000; // refresh at 50 min (was 55) - safety margin

const PUBLIC_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password', '/role-select'];

const SessionMonitor = () => {
  const navigate   = useNavigate();
  const location   = useLocation();

  // Keep a ref to current pathname so the effect never needs pathname as a dependency
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // Track when the session/token was last refreshed
  const sessionStartRef = useRef(Date.now());

  useEffect(() => {
    // ── Activity tracking ────────────────────────────────────────────────────
    const updateActivity = () => {
      const now = Date.now();
      try { localStorage.setItem('lastActivityTime', now.toString()); } catch {}
    };

    const getLastActivity = () => {
      try {
        const stored = localStorage.getItem('lastActivityTime');
        return stored ? parseInt(stored, 10) : Date.now();
      } catch {
        return Date.now();
      }
    };

    // ── Session expiry ───────────────────────────────────────────────────────
    const handleSessionExpired = () => {
      try { localStorage.removeItem('hopeSaccoToken'); }   catch {}
      try { localStorage.removeItem('hopeSaccoUser'); }    catch {}
      try { localStorage.removeItem('lastActivityTime'); } catch {}

      if (pathnameRef.current !== '/login') {
        try { sessionStorage.setItem('redirectAfterLogin', pathnameRef.current); } catch {}
      }
      navigate('/login', { replace: true });
    };

    // ── Proactive Firebase token refresh ─────────────────────────────────────
    // Firebase tokens expire every 60 min. We refresh at 50 min (safety margin)
    // so the token is always fresh before it expires, preventing spurious 401s.
    const refreshFirebaseToken = async () => {
      try {
        const { getAuth } = await import('firebase/auth');
        const user = getAuth().currentUser;
        if (!user) return;
        const fresh = await user.getIdToken(true);
        try { localStorage.setItem('hopeSaccoToken', fresh); } catch {}
        sessionStartRef.current = Date.now(); // reset the 50-min clock
        console.log('✅ Firebase token proactively refreshed by SessionMonitor');
      } catch (err) {
        console.warn('Proactive token refresh failed:', err?.message || err);
        // Don't log out here — globalFetchWrapper handles genuine expiry on next API call
      }
    };

    // ── Periodic check ───────────────────────────────────────────────────────
    const checkSession = async () => {
      if (PUBLIC_PATHS.includes(pathnameRef.current)) return;

      const token = localStorage.getItem('hopeSaccoToken');
      if (!token) return;

      const now             = Date.now();
      const inactiveTime    = now - getLastActivity();
      const minutesInactive = Math.floor(inactiveTime / 60000);

      // 1. Log out if inactive for over 60 minutes
      if (inactiveTime > INACTIVITY_TIMEOUT) {
        console.log(`🔒 Inactivity timeout (${minutesInactive} min) — logging out`);
        handleSessionExpired();
        return;
      }

      console.log(`✅ Session active (inactive for ${minutesInactive} min)`);

      // 2. Proactively refresh the Firebase token before it hits 60 min
      const timeSinceRefresh = now - sessionStartRef.current;
      if (timeSinceRefresh >= TOKEN_REFRESH_THRESHOLD) {
        console.log('🔄 Time to refresh token proactively (50 min since last refresh)');
        await refreshFirebaseToken();
      }
    };

    // ── Multi-tab synchronization ────────────────────────────────────────────
    // Listen for when another tab logs out or signals session expiry
    const handleStorageChange = (event) => {
      // Another tab cleared the token
      if (event.key === 'hopeSaccoToken' && !event.newValue) {
        console.log('👀 Another tab cleared the token - syncing logout');
        handleSessionExpired();
        return;
      }

      // Another tab signaled session expiry via globalFetchWrapper
      if (event.key === 'sessionExpired') {
        console.log('👀 Another tab signaled session expiry - syncing');
        handleSessionExpired();
        return;
      }

      // Another tab cleared user data
      if (event.key === 'hopeSaccoUser' && !event.newValue) {
        console.log('👀 Another tab cleared user data - syncing logout');
        handleSessionExpired();
        return;
      }
    };

    // ── Setup ─────────────────────────────────────────────────────────────────
    if (PUBLIC_PATHS.includes(pathnameRef.current)) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => document.addEventListener(evt, updateActivity, true));

    // Add storage listener for multi-tab sync
    window.addEventListener('storage', handleStorageChange);

    updateActivity();
    sessionStartRef.current = Date.now();

    console.log('🚀 SessionMonitor started (60-min inactivity, 50-min token refresh, multi-tab sync enabled)');

    const intervalId = setInterval(checkSession, CHECK_INTERVAL);

    return () => {
      activityEvents.forEach(evt => document.removeEventListener(evt, updateActivity, true));
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };

  // ⚠️  Only [navigate] — NOT location.pathname.
  // Adding pathname would rebuild the effect (and reset the clock) on every page change.
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default SessionMonitor;
